import { gql, useMutation } from '@apollo/client';
import { Refresh } from '@mui/icons-material';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Alert } from '@ovComponents/2-component/alert/alert';
import { Button } from '@ovComponents/2-component/button/button';
import { CircularProgress } from '@ovComponents/2-component/circularProgress/circularProgress';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const PREPARE_FORM_COMPLETIONS = gql`
  mutation prepareFormCompletions($input: PrepareFormCompletionsInput!) {
    prepareFormCompletions(input: $input) {
      formCompletions {
        id
        state
      }
    }
  }
`;

export const PrepareFormCompletions = ({
  onNext,
  onBack,
  workflowCompletion,
  direction,
}: {
  onNext: () => void;
  onBack: () => void;
  workflowCompletion: any;
  direction: 'FORWARD' | 'BACKWARD';
}) => {
  const { t } = useTranslation('workflowCompletions');
  const { workflowData } = useContext(WorkflowContext);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const onNextRef = useRef(onNext);
  const onBackRef = useRef(onBack);
  const hasAttemptedRef = useRef(false);
  const hasAdvancedRef = useRef(false);

  onNextRef.current = onNext;
  onBackRef.current = onBack;

  const advance = useCallback(() => {
    if (hasAdvancedRef.current) {
      return;
    }
    hasAdvancedRef.current = true;
    onNextRef.current();
  }, []);

  const [prepareFormCompletions] = useMutation(PREPARE_FORM_COMPLETIONS, {
    onCompleted: () => {
      setError(null);
      advance();
    },
  });

  const attemptPrepareFormCompletions = useCallback(async () => {
    const formMapping = (
      workflowData.formMapping ?? workflowCompletion.context?.formMapping
    )?.filter(
      (mapping: { objectId: string | null }) => mapping.objectId !== null,
    );

    if (!workflowCompletion.organization?.id) {
      return;
    }

    if (!formMapping?.length) {
      advance();
      return;
    }

    setIsLoading(true);
    setError(null);

    // Set by RegenerateForms when the user chose to cancel the current signing
    // package and regenerate. Clears itself naturally on the next
    // setActiveWorkflowCompletion call (e.g. when nextStep advances).
    const forcePrepare = Boolean(
      workflowCompletion.forcePrepareFormCompletions,
    );

    const variables = {
      input: {
        workflowCompletionId: workflowCompletion.id,
        userId: workflowCompletion.objectId,
        organizationId: workflowCompletion.organization.id,
        formMapping,
        ...(forcePrepare && { forcePrepare: true }),
      },
      skipErrorHandler: true,
    };

    try {
      await prepareFormCompletions({ variables });
      return;
    } catch (err) {
      setError(err as Error);
    }
    setIsLoading(false);
  }, [
    workflowCompletion,
    workflowData.formMapping,
    prepareFormCompletions,
    advance,
  ]);

  useEffect(() => {
    if (hasAttemptedRef.current) {
      return;
    }
    hasAttemptedRef.current = true;
    // When the user navigates BACKWARD into this step, skip the (now-instant)
    // idempotent prepare mutation and continue going back. Otherwise the
    // on-mount mutation auto-advances onNext, which would either silently
    // bounce the user forward again or race the user's next back-press and
    // produce a Mongoose VersionError on the WorkflowCompletion document.
    // Same pattern as SynchronousIntegration / depositType.
    if (direction === 'BACKWARD') {
      onBackRef.current();
      return;
    }
    void attemptPrepareFormCompletions();
  }, [attemptPrepareFormCompletions, direction]);

  return (
    <Box
      display='flex'
      flexDirection='column'
      alignItems='center'
      justifyContent='center'
      gap={1}
      minHeight='300px'
    >
      <Typography variant='headingMedium'>
        {t('prepareFormCompletions.title')}
      </Typography>
      <Typography variant='bodyMedium' sx={{ mb: 4 }}>
        {t('prepareFormCompletions.description')}
      </Typography>
      {error && (
        <Box mb={2}>
          <Alert severity='error'>{error.message}</Alert>
          <Box mt={2} display='flex' justifyContent='center'>
            <Button
              onClick={attemptPrepareFormCompletions}
              disabled={isLoading}
              label={t('shared:retry')}
              variant='text'
              leadingIcon={Refresh}
            />
          </Box>
        </Box>
      )}
      {isLoading && <CircularProgress />}
    </Box>
  );
};
